import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../shared/_TBComponents/Button';
import Modal from '../../../../shared/_TBComponents/Modal';
import Text from '../../../../shared/_TBComponents/Text';
import { showSuccessToast, showErrorToast } from '../../../../shared/utils/toastManager';
import {
  archiveUser,
  deleteArchivedUser,
  deleteUsers,
  resendActivationEmail,
  unarchiveUser,
} from '../../ducks/navigatorActions';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: #F7F7F7;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const ManageAthletesModals = ({
  accountCode,
  athleteToDelete,
  athleteToResend,
  athleteToArchive,
  athleteToRestore,
  isArchived,
  onClearDelete,
  onClearResend,
  onClearArchive,
  onClearRestore,
  onRefetch,
  onRefetchArchived,
}) => {
  const dispatch = useDispatch();

  const isDeletingUsers = useSelector((state) => state.navigator.ui.isDeletingUsers);
  const isArchivingUser = useSelector((state) => state.navigator.ui.isArchivingUser);
  const isUnarchivingUser = useSelector((state) => state.navigator.ui.isUnarchivingUser);
  const usernamesEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.generalSettings?.usernamesEnabled,
  );

  const [isSendingActivation, setIsSendingActivation] = useState(false);

  // Keep last selected athlete available while related modals or actions are rendering
  const resendRef = useRef(null);
  const deleteRef = useRef(null);
  const archiveRef = useRef(null);
  const restoreRef = useRef(null);

  if (athleteToResend) resendRef.current = athleteToResend;
  if (athleteToDelete) deleteRef.current = athleteToDelete;
  if (athleteToArchive) archiveRef.current = athleteToArchive;
  if (athleteToRestore) restoreRef.current = athleteToRestore;

  const displayResend = resendRef.current;
  const displayDelete = deleteRef.current;
  const displayArchive = archiveRef.current;
  const displayRestore = restoreRef.current;

  return (
    <>
      <Modal
        isOpen={!!athleteToResend}
        onRequestClose={onClearResend}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
      >
        <ContentWrapper>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Resend Activation
          </Text>
          <InfoWrapper>
            <Text styledTextProps={{ fontSize: '14px' }}>
              {usernamesEnabled
                ? `${displayResend?.first} ${displayResend?.last}`
                : `Send to: ${displayResend?.first} ${displayResend?.last}`}
            </Text>
          </InfoWrapper>
          {usernamesEnabled ? (
            <Text styledWrapperProps={{ marginBottom: '24px' }}>
              Registration emails cannot be sent when usernames are enabled. To give this athlete access, reset their password and provide it to them directly.
            </Text>
          ) : (
            <Text styledWrapperProps={{ marginBottom: '24px' }}>
              A new random password will be generated and sent to the selected athlete.
            </Text>
          )}
          <Footer>
            <Button
              name='Close'
              fontSize='14px'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={onClearResend}
            />
            {!usernamesEnabled && (
              <Button
                name={isSendingActivation ? 'Sending...' : 'Resend'}
                fontSize='14px'
                txtColor='#FFF'
                bgColor='#444444'
                borderColor='#444444'
                customBorderRadius='6px'
                styledTextProps={{ fontWeight: 600 }}
                disabled={isSendingActivation}
                onClick={() => {
                  setIsSendingActivation(true);
                  dispatch(resendActivationEmail(accountCode, athleteToResend.id, {
                    onSuccess: () => {
                      showSuccessToast('Activation email resent.');
                      onClearResend();
                      setIsSendingActivation(false);
                    },
                    onError: () => {
                      showErrorToast('Failed to resend activation email. Please try again.');
                      setIsSendingActivation(false);
                    },
                  }));
                }}
              />
            )}
          </Footer>
        </ContentWrapper>
      </Modal>

      <Modal
        isOpen={!!athleteToArchive}
        onRequestClose={onClearArchive}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
      >
        <ContentWrapper>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Archive Athlete
          </Text>
          <Text styledWrapperProps={{ marginBottom: '24px' }}>
            {`Are you sure you want to archive ${displayArchive?.first} ${displayArchive?.last}? 
            They will no longer appear in the active athletes list.`}
          </Text>
          <Footer>
            <Button
              name='Cancel'
              fontSize='14px'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={onClearArchive}
            />
            <Button
              name={isArchivingUser ? 'Archiving...' : 'Archive'}
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#444444'
              borderColor='#444444'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              disabled={isArchivingUser}
              onClick={() => dispatch(archiveUser(accountCode, athleteToArchive.id, {
                onSuccess: () => {
                  showSuccessToast(`${athleteToArchive.first} ${athleteToArchive.last} has been archived.`);
                  onRefetch();
                  onRefetchArchived();
                  onClearArchive();
                },
                onError: () => {
                  showErrorToast('Failed to archive athlete. Please try again.');
                },
              }))}
            />
          </Footer>
        </ContentWrapper>
      </Modal>

      <Modal
        isOpen={!!athleteToRestore}
        onRequestClose={onClearRestore}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
      >
        <ContentWrapper>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Restore Athlete
          </Text>
          <Text styledWrapperProps={{ marginBottom: '24px' }}>
            {`Are you sure you want to restore ${displayRestore?.first} ${displayRestore?.last}? 
            They will be moved back to the active athletes list.`}
          </Text>
          <Footer>
            <Button
              name='Cancel'
              fontSize='14px'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={onClearRestore}
            />
            <Button
              name={isUnarchivingUser ? 'Restoring...' : 'Restore'}
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#444444'
              borderColor='#444444'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              disabled={isUnarchivingUser}
              onClick={() => dispatch(unarchiveUser(accountCode, athleteToRestore.userId, {
                onSuccess: () => {
                  showSuccessToast(`${athleteToRestore.first} ${athleteToRestore.last} has been restored.`);
                  onRefetch();
                  onClearRestore();
                },
                onError: () => {
                  showErrorToast('Failed to restore athlete. Please try again.');
                },
              }))}
            />
          </Footer>
        </ContentWrapper>
      </Modal>

      <Modal
        isOpen={!!athleteToDelete}
        onRequestClose={onClearDelete}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
      >
        <ContentWrapper>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Delete Athlete
          </Text>
          <Text styledWrapperProps={{ marginBottom: '24px' }}>
            {`Are you sure you want to delete ${displayDelete?.first} ${displayDelete?.last}? 
            This action cannot be undone.`}
          </Text>
          <Footer>
            <Button
              name='Cancel'
              fontSize='14px'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={onClearDelete}
            />
            <Button
              name={isDeletingUsers ? 'Deleting...' : 'Delete'}
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#FF0505'
              borderColor='#FF0505'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              disabled={isDeletingUsers}
              onClick={() => {
                const userId = athleteToDelete.userId ?? athleteToDelete.id;
                const action = isArchived
                  ? deleteArchivedUser(accountCode, userId, {
                    onSuccess: () => {
                      showSuccessToast(`${athleteToDelete.first} ${athleteToDelete.last} has been deleted.`);
                      onRefetchArchived();
                      onClearDelete();
                    },
                    onError: () => {
                      showErrorToast('Failed to delete athlete. Please try again.');
                    },
                  })
                  : deleteUsers(accountCode, [userId], {
                    onSuccess: () => {
                      showSuccessToast(`${athleteToDelete.first} ${athleteToDelete.last} has been deleted.`);
                      onRefetch();
                      onClearDelete();
                    },
                    onError: () => {
                      showErrorToast('Failed to delete athlete. Please try again.');
                    },
                  });
                dispatch(action);
              }}
            />
          </Footer>
        </ContentWrapper>
      </Modal>
    </>
  );
};

const athleteShape = PropTypes.shape({
  first: PropTypes.string,
  last: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

ManageAthletesModals.propTypes = {
  accountCode: PropTypes.string,
  athleteToDelete: athleteShape,
  athleteToResend: athleteShape,
  athleteToArchive: athleteShape,
  athleteToRestore: PropTypes.shape({
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first: PropTypes.string,
    last: PropTypes.string,
  }),
  isArchived: PropTypes.bool,
  onClearDelete: PropTypes.func.isRequired,
  onClearResend: PropTypes.func.isRequired,
  onClearArchive: PropTypes.func.isRequired,
  onClearRestore: PropTypes.func.isRequired,
  onRefetch: PropTypes.func.isRequired,
  onRefetchArchived: PropTypes.func.isRequired,
};

ManageAthletesModals.defaultProps = {
  accountCode: null,
  athleteToDelete: null,
  athleteToResend: null,
  athleteToArchive: null,
  athleteToRestore: null,
  isArchived: false,
};

export default ManageAthletesModals;
